import React from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { FileText, Shield, Info, Mail, Phone, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

const Logo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div className="p-1 rounded-lg bg-blue-600">
              <Logo className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-blue-600">Quick Hire</span>
          </div>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="rounded-full px-6"
          >
            Back to Home
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-4xl mx-auto px-6 py-12 text-center">
        <Badge className="mb-4" variant="secondary">Legal Information</Badge>
        <h1 className="text-5xl font-bold text-gray-900 mb-4">
          Privacy Policy
        </h1>
        <p className="text-lg text-gray-600 mb-4">
          Last Updated: November 24, 2025
        </p>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Your privacy is important to us. This Privacy Policy describes how Quick Hire collects, uses, shares, and protects your information when you use our resume building platform.
        </p>
      </section>

      {/* What Data We Collect */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-bold mb-8 text-gray-900">Information We Collect</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="p-6 text-center hover:shadow-xl transition-all">
            <Users className="h-7 w-7 text-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Personal Data</h3>
            <p className="text-gray-600">Name, email address, account login details, payment information.</p>
          </Card>
          <Card className="p-6 text-center hover:shadow-xl transition-all">
            <Info className="h-7 w-7 text-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Usage Data</h3>
            <p className="text-gray-600">Pages visited, actions on the platform, resume content, device & browser details.</p>
          </Card>
          <Card className="p-6 text-center hover:shadow-xl transition-all">
            <Shield className="h-7 w-7 text-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Security Data</h3>
            <p className="text-gray-600">Technical logs, error reports, session and authentication data.</p>
          </Card>
        </div>
      </section>

      {/* Detailed Policy */}
      <section className="max-w-4xl mx-auto px-6 py-12">
        <Card className="p-8">
          <h2 className="text-2xl font-bold mb-6 text-gray-900">How We Use Your Data</h2>
          <div className="space-y-6 text-gray-700">
            <div>
              <strong>To provide and improve our services:</strong> Creating accounts, storing resumes, customizing user experiences, and processing payments.
            </div>
            <div>
              <strong>For customer support:</strong> Responding to your inquiries and addressing issues efficiently.
            </div>
            <div>
              <strong>To comply with legal obligations:</strong> Meeting regulatory requirements and enforcing our Terms of Service.
            </div>
            <div>
              <strong>For analytics and marketing:</strong> Understanding usage patterns, platform improvement, and occasional relevant offers (you can opt out).
            </div>
          </div>

          <h2 className="text-2xl font-bold mt-8 mb-4 text-gray-900">How We Share Data</h2>
          <ul className="list-disc list-inside space-y-2 ml-4 text-gray-700">
            <li>
              <strong>Third-party Payment Processors:</strong> Securely handling payments.
            </li>
            <li>
              <strong>Analytics Providers:</strong> Usage analytics (Google Analytics, etc.).
            </li>
            <li>
              <strong>Legal and Regulatory Authorities:</strong> If required by law, for fraud or safety purposes.
            </li>
            <li>
              <strong>No Sale of Data:</strong> We do not sell your personal data to any third party.
            </li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-4 text-gray-900">How We Protect Your Data</h2>
          <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
            <li>Encryption at rest and in transit for sensitive information.</li>
            <li>Role-based access control; only authorized staff access personal data.</li>
            <li>Regular security audits and updates.</li>
            <li>User authentication and secure session management.</li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-4 text-gray-900">Your Rights</h2>
          <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
            <li>
              <strong>Access & Correction:</strong> You can access, update, or correct your personal info anytime.
            </li>
            <li>
              <strong>Erase Data:</strong> Request deletion of your account and associated personal data.
            </li>
            <li>
              <strong>Withdraw Consent:</strong> You may withdraw consent for non-essential data collection.
            </li>
            <li>
              <strong>Data Portability:</strong> Request an export of your data.
            </li>
            <li>
              <strong>Complaints:</strong> Contact regulatory authorities if you feel your data is mishandled.
            </li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-4 text-gray-900">Cookies & Tracking</h2>
          <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
            <li>
              We use cookies for authentication, user preferences, and analytics. You can manage cookie settings in your browser.
            </li>
            <li>
              No cookies used for tracking across third-party sites.
            </li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-4 text-gray-900">Data Retention</h2>
          <div className="text-gray-700 mb-3">
            We retain your personal data for as long as necessary to provide our services and fulfill legal obligations. You can request account deletion at any time.
          </div>

          <h2 className="text-2xl font-bold mt-8 mb-4 text-gray-900">Children's Privacy</h2>
          <div className="text-gray-700 mb-3">
            Our services are not intended for children under 16. We do not knowingly collect data from minors.
          </div>
        </Card>
      </section>

      {/* Accordion FAQ */}
      <section className="bg-gradient-to-tr from-indigo-50 via-blue-100 to-white py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
            Privacy <Badge className="ml-2" variant="default">FAQs</Badge>
          </h2>
          <Accordion type="single" collapsible>
            <AccordionItem value="privacy-1">
              <AccordionTrigger>
                Can I delete my account and personal data?
              </AccordionTrigger>
              <AccordionContent>
                Yes. You may request deletion of your account and personal data at any time. Contact support for assistance.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="privacy-2">
              <AccordionTrigger>
                Who can access my personal information?
              </AccordionTrigger>
              <AccordionContent>
                Only authorized Quick Hire staff and select third-party processors (e.g., payment partners) needed for service provision.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="privacy-3">
              <AccordionTrigger>
                How do I update my information?
              </AccordionTrigger>
              <AccordionContent>
                You can update your profile details in your dashboard at any time.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="privacy-4">
              <AccordionTrigger>
                Is my information shared outside Nigeria?
              </AccordionTrigger>
              <AccordionContent>
                Some data may be stored using international cloud providers, but always under strict legal contracts and security measures.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="privacy-5">
              <AccordionTrigger>
                How can I contact the Data Protection Officer?
              </AccordionTrigger>
              <AccordionContent>
                Email <a href="mailto:support@quick-hire.org" className="text-blue-600 hover:underline font-semibold">support@quick-hire.org</a> to reach our data protection contact.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <Card className="p-8 bg-blue-600 text-white">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-4">Questions About Privacy?</h2>
            <p className="text-blue-100 mb-6 text-lg">
              Reach out to our Data Protection contact for privacy and data inquiries.
            </p>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-6">
              <div className="flex items-center space-x-2">
                <Mail className="h-5 w-5" />
                <a href="mailto:support@quick-hire.org" className="hover:underline">
                  support@quick-hire.org
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="h-5 w-5" />
                <a href="tel:+353832007206" className="hover:underline">
                  +353832007206
                </a>
              </div>
            </div>
            <Button
              onClick={() => window.location.href = 'mailto:support@quick-hire.org'}
              className="bg-white text-blue-600 hover:bg-blue-50 rounded-full px-8 py-6 text-lg"
            >
              Contact Data Protection
            </Button>
          </div>
        </Card>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
